import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Pencil, Store, ShoppingCart, Save, X, ChevronDown, ChevronUp, ListChecks, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { pricingAPI } from '@/lib/api';

const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatPct = (value) => {
    if (value == null || isNaN(value)) return '-';
    return `${value.toFixed(1)}%`;
};

const formatCoef = (value) => {
    if (value == null || isNaN(value) || value === 0) return '-';
    return `x${value.toFixed(2)}`;
};

// ─── Client Form Dialog ──────────────────────────────────────────────
function ClientFormDialog({ open, onClose, client, onSaved }) {
    const [name, setName] = useState('');
    const [saleType, setSaleType] = useState('direct');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(client?.name || '');
            setSaleType(client?.sale_type || 'direct');
        }
    }, [open, client]);

    const handleSave = async () => {
        if (!name.trim()) { toast.error('Le nom est requis'); return; }
        setSaving(true);
        try {
            if (client?.id) {
                await pricingAPI.updateClient(client.id, { name: name.trim(), sale_type: saleType });
                toast.success('Client modifié');
            } else {
                await pricingAPI.createClient({ name: name.trim(), sale_type: saleType });
                toast.success('Client créé');
            }
            onSaved();
            onClose();
        } catch {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-md" data-testid="client-form-dialog">
                <DialogHeader>
                    <DialogTitle>{client?.id ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
                    <DialogDescription>Définissez le nom et le type de vente.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div>
                        <Label>Nom du client</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex: Restaurant X, Carrefour Martinique..."
                            data-testid="client-name-input"
                        />
                    </div>
                    <div>
                        <Label>Type de vente</Label>
                        <Select value={saleType} onValueChange={setSaleType}>
                            <SelectTrigger data-testid="client-sale-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="direct">
                                    <span className="flex items-center gap-2"><Store className="w-3.5 h-3.5" /> Vente directe</span>
                                </SelectItem>
                                <SelectItem value="carrefour">
                                    <span className="flex items-center gap-2"><ShoppingCart className="w-3.5 h-3.5" /> Carrefour (commission 15%)</span>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={handleSave} disabled={saving} data-testid="client-save-btn">
                        {client?.id ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Client Price Table ──────────────────────────────────────────────
function ClientPriceTable({ clientId, client }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editedPrices, setEditedPrices] = useState({});
    const [toggledEnabled, setToggledEnabled] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState('active'); // 'all' or 'active'
    const clientIdRef = useRef(clientId);
    clientIdRef.current = clientId;

    const fetchPrices = useCallback(async () => {
        try {
            setLoading(true);
            setData(null);
            const res = await pricingAPI.getClientPrices(clientId);
            if (clientIdRef.current !== clientId) return;
            setData(res.data);
            setEditedPrices({});
            setToggledEnabled({});
            setHasChanges(false);
        } catch {
            toast.error('Erreur lors du chargement des prix');
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { fetchPrices(); }, [fetchPrices]);

    const updatePrice = (recipeId, value) => {
        setEditedPrices(prev => ({ ...prev, [recipeId]: value }));
        setHasChanges(true);
    };

    const toggleEnabled = (recipeId, currentEnabled) => {
        const newEnabled = !currentEnabled;
        setToggledEnabled(prev => ({ ...prev, [recipeId]: newEnabled }));
        setHasChanges(true);
    };

    const isEnabled = (r) => {
        if (toggledEnabled[r.recipe_id] !== undefined) return toggledEnabled[r.recipe_id];
        return r.enabled;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const prices = [];
            // Collect price changes
            for (const [recipe_id, val] of Object.entries(editedPrices)) {
                prices.push({
                    recipe_id,
                    price_ht: val === '' ? 0 : parseFloat(val) || 0,
                });
            }
            // Collect enabled changes
            for (const [recipe_id, enabled] of Object.entries(toggledEnabled)) {
                const existing = prices.find(p => p.recipe_id === recipe_id);
                if (existing) {
                    existing.enabled = enabled;
                } else {
                    prices.push({ recipe_id, enabled });
                }
            }
            await pricingAPI.updateClientPrices(clientId, prices);
            toast.success('Prix enregistrés');
            fetchPrices();
        } catch {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-center py-8 text-slate-400 text-sm">Chargement...</div>;
    if (!data) return null;

    const isCarrefour = client?.sale_type === 'carrefour';
    const commission = isCarrefour ? 0.15 : (client?.commission || 0);
    const hasCommission = commission > 0;
    const allRecipes = data.recipes || [];
    const visibleRecipes = viewMode === 'active' ? allRecipes.filter(r => isEnabled(r)) : allRecipes;
    const enabledCount = allRecipes.filter(r => isEnabled(r)).length;

    return (
        <div>
            {/* View mode toggle */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
                    <button
                        onClick={() => setViewMode('active')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                            viewMode === 'active' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid="view-active-btn"
                    >
                        <ListChecks className="w-3.5 h-3.5" />
                        Sélection ({enabledCount})
                    </button>
                    <button
                        onClick={() => setViewMode('all')}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                            viewMode === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid="view-all-btn"
                    >
                        <List className="w-3.5 h-3.5" />
                        Toutes les recettes ({allRecipes.length})
                    </button>
                </div>
                {hasChanges && (
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={fetchPrices} data-testid="price-cancel-btn">
                            <X className="w-4 h-4 mr-1" /> Annuler
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="price-save-btn">
                            <Save className="w-4 h-4 mr-1" /> Enregistrer
                        </Button>
                    </div>
                )}
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            {viewMode === 'all' && (
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 w-10"></TableHead>
                            )}
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500">Recette</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500">Catégorie</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Coût revient</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right w-32">
                                {isCarrefour ? 'Prix vente TTC' : 'Prix vente HT'}
                            </TableHead>
                            {hasCommission && (
                                <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Net HT (après {Math.round(commission * 100)}%)</TableHead>
                            )}
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Marge</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Marge %</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider font-medium text-slate-500 text-right">Coef</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {visibleRecipes.map((r) => {
                            const enabled = isEnabled(r);
                            const currentPrice = editedPrices[r.recipe_id] !== undefined
                                ? editedPrices[r.recipe_id]
                                : (r.price_ht != null ? r.price_ht : '');
                            const priceNum = parseFloat(currentPrice) || 0;

                            let effectiveHt = 0, marginEur = 0, marginPct = 0, coef = 0;
                            if (priceNum > 0) {
                                // Carrefour : TTC → net = TTC*0.85 / 1.021 ; commission HT (ex Socoarmes 20%) : net = HT * (1-com)
                                effectiveHt = isCarrefour ? (priceNum * 0.85) / 1.021 : hasCommission ? priceNum * (1 - commission) : priceNum;
                                marginEur = effectiveHt - r.cost_per_unit;
                                marginPct = effectiveHt > 0 ? (marginEur / effectiveHt) * 100 : 0;
                                coef = r.cost_per_unit > 0 ? effectiveHt / r.cost_per_unit : 0;
                            }

                            const marginColor = marginEur > 0 ? 'text-emerald-600' : marginEur < 0 ? 'text-red-600' : 'text-slate-400';
                            const rowOpacity = viewMode === 'all' && !enabled ? 'opacity-50' : '';

                            return (
                                <TableRow key={r.recipe_id} className={`hover:bg-slate-50/50 ${rowOpacity}`} data-testid={`price-row-${r.recipe_id}`}>
                                    {viewMode === 'all' && (
                                        <TableCell className="px-3">
                                            <input
                                                type="checkbox"
                                                checked={enabled}
                                                onChange={() => toggleEnabled(r.recipe_id, enabled)}
                                                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                data-testid={`enable-recipe-${r.recipe_id}`}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell className="font-medium text-sm">{r.recipe_name}</TableCell>
                                    <TableCell>
                                        {r.category ? (
                                            <span className="inline-block bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{r.category}</span>
                                        ) : <span className="text-xs text-slate-300">-</span>}
                                    </TableCell>
                                    <TableCell className="text-right text-sm text-slate-600">
                                        {formatCurrency(r.cost_per_unit)}/{r.yield_unit || 'u.'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            className="h-8 w-28 text-sm text-right ml-auto"
                                            value={currentPrice}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '' || /^\d*\.?\d*$/.test(v)) updatePrice(r.recipe_id, v);
                                            }}
                                            placeholder="-"
                                            data-testid={`price-input-${r.recipe_id}`}
                                        />
                                    </TableCell>
                                    {hasCommission && (
                                        <TableCell className="text-right text-sm text-slate-500">
                                            {priceNum > 0 ? formatCurrency(effectiveHt) : '-'}
                                        </TableCell>
                                    )}
                                    <TableCell className={`text-right text-sm font-semibold ${marginColor}`}>
                                        {priceNum > 0 ? formatCurrency(marginEur) : '-'}
                                    </TableCell>
                                    <TableCell className={`text-right text-sm ${marginColor}`}>
                                        {priceNum > 0 ? formatPct(marginPct) : '-'}
                                    </TableCell>
                                    <TableCell className={`text-right text-sm ${marginColor}`}>
                                        {priceNum > 0 ? formatCoef(coef) : '-'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {visibleRecipes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={viewMode === 'all' ? (isCarrefour ? 9 : 8) : (isCarrefour ? 8 : 7)} className="text-center text-slate-400 py-6">
                                    {viewMode === 'active' ? 'Aucune recette sélectionnée — passez en mode "Toutes les recettes" pour cocher les produits vendus' : 'Aucune recette trouvée'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ─── Main Pricing Panel ──────────────────────────────────────────────
export default function PricingPanel() {
    const [clients, setClients] = useState([]);
    const [activeClientId, setActiveClientId] = useState(null);
    const [showClientForm, setShowClientForm] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchClients = useCallback(async () => {
        try {
            const res = await pricingAPI.getClients();
            setClients(res.data);
            if (res.data.length > 0 && !activeClientId) {
                setActiveClientId(res.data[0].id);
            }
        } catch { /* ignore */ }
    }, [activeClientId]);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    const handleDeleteClient = async () => {
        if (!deleteTarget) return;
        try {
            await pricingAPI.deleteClient(deleteTarget.id);
            toast.success('Client supprimé');
            setDeleteTarget(null);
            if (activeClientId === deleteTarget.id) {
                setActiveClientId(clients.find(c => c.id !== deleteTarget.id)?.id || null);
            }
            fetchClients();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const activeClient = clients.find(c => c.id === activeClientId);

    return (
        <div className="space-y-4" data-testid="pricing-panel">
            {/* Client tabs */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex gap-1 bg-slate-200/70 rounded-lg p-1 flex-wrap">
                    {clients.map(c => (
                        <button
                            key={c.id}
                            onClick={() => setActiveClientId(c.id)}
                            className={`px-4 py-2 text-sm font-semibold rounded-md transition-all flex items-center gap-1.5 ${
                                activeClientId === c.id
                                    ? 'bg-slate-900 text-white shadow-md'
                                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                            }`}
                            data-testid={`client-tab-${c.id}`}
                        >
                            {c.sale_type === 'carrefour'
                                ? <ShoppingCart className={`w-3.5 h-3.5 ${activeClientId === c.id ? 'text-blue-300' : 'text-blue-500'}`} />
                                : <Store className={`w-3.5 h-3.5 ${activeClientId === c.id ? 'text-emerald-300' : 'text-emerald-500'}`} />}
                            {c.name}
                        </button>
                    ))}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditingClient(null); setShowClientForm(true); }}
                    data-testid="add-client-btn"
                >
                    <Plus className="w-4 h-4 mr-1" /> Client
                </Button>
                {activeClient && (
                    <div className="flex gap-1 ml-auto">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-blue-600"
                            onClick={() => { setEditingClient(activeClient); setShowClientForm(true); }}
                            data-testid="edit-client-btn"
                        >
                            <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500"
                            onClick={() => setDeleteTarget(activeClient)}
                            data-testid="delete-client-btn"
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </div>

            {/* Client info badge */}
            {activeClient && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    {activeClient.sale_type === 'carrefour' ? (
                        <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">
                            Carrefour — commission 15% déduite | TVA 2,1%
                        </span>
                    ) : activeClient.commission > 0 ? (
                        <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">
                            {activeClient.name} — prix saisi HT, commission {Math.round(activeClient.commission * 100)}% déduite
                        </span>
                    ) : (
                        <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-medium">
                            Vente directe HT
                        </span>
                    )}
                </div>
            )}

            {/* Price table */}
            {activeClientId ? (
                <ClientPriceTable clientId={activeClientId} client={activeClient} />
            ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                    <Store className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Ajoutez un client pour commencer à définir les tarifs</p>
                </div>
            )}

            {/* Client form dialog */}
            <ClientFormDialog
                open={showClientForm}
                onClose={() => setShowClientForm(false)}
                client={editingClient}
                onSaved={fetchClients}
            />

            {/* Delete confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent data-testid="delete-client-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer le client</AlertDialogTitle>
                        <AlertDialogDescription>
                            Supprimer "{deleteTarget?.name}" et tous ses tarifs associés ?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteClient} className="bg-red-600 hover:bg-red-700">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
